<template>
  <div class="main-space">
    <LegacyWrap>
      <h2 class="title">{{ t('title') }}</h2>
      <div class="subtitle">{{ t('subtitle') }}</div>
    </LegacyWrap>
    <div class="swiper-main-container">
      <UiSlider v-bind="cardProps" :key="regions?.length" arrows-always-visible>
        <template #skeleton><Skeleton /></template>
        <template #slide="{ slide, index }">
          <card
            v-view-tracker="getParams(slide, index)"
            v-click-tracker="getParams(slide, index)"
            :index="index"
            :slide="slide"
            class="slide"
          />
        </template>
        <template #rightArrowIcon>
          <IconBase name="right-arrow" color="--alias-color-icon-subtle" />
        </template>
        <template #leftArrowIcon>
          <IconBase class="mirror" name="right-arrow" color="--alias-color-icon-subtle" />
        </template>
      </UiSlider>
    </div>

    <LegacyWrap class="center">
      <UiButton
        v-click-btn-tracker
        data-test="button-articles-more"
        class="btn"
        :variant="UiButtonVariant.OUTLINE"
        :color-mode="UiButtonColorMode.NEUTRAL"
        :text="t('viewAll')"
        :is-full-width="isMobileOrDesktop"
        href="/tours/"
      />
    </LegacyWrap>
  </div>
</template>

<script setup lang="ts">
import { UiButton, UiButtonColorMode, UiButtonVariant } from '@experience/core-button-ui';
import { getDisplayCurrencyQuery } from '@experience/core-display-currency-lib';
import { UiSlider } from '@experience/core-slider-ui';
import { useI18n } from '@experience/i18n';
import { computed, ref, onMounted } from 'vue';

import ru from '@/features/toursList/i18n/ru';
import Card from '@/features/toursList/TourCard.vue';
import Skeleton from '@/features/toursList/TourCardSkeleton.vue';
import { TOURS_REGION_SLUGS } from '@/features/ToursList.config';
import { getRegionsList } from '@/shared/api/generated/api';
import { recursiveToCamel } from '@/shared/helpers/toCamel';
import IconBase from '@/shared/icons/IconBase.vue';
import LegacyWrap from '@/shared/LegacyWrap.vue';
import { useAnalytics } from '@/shared/lib/analytics';
import { useCarouselConfig } from '@/shared/lib/carousel/useCarouselConfig';
import { type ToursCard } from '@/shared/models/tours';
import { useScreenSize } from '@/shared/utils/breakpoints';

type MessageSchema = typeof ru;
const { t } = useI18n<{ message: MessageSchema }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const breakpoints = useScreenSize();

const { useViewTracker, useImmediateClickTracker } = useAnalytics();
const vViewTracker = useViewTracker('main:tours:tour.view');
const vClickTracker = useImmediateClickTracker('main:tours:tour.click');
const vClickBtnTracker = useImmediateClickTracker('main:tours:show-all-button.click');

const regions = ref<ToursCard[]>([]);

const isMobileOrDesktop = computed(() => {
  return breakpoints.isTablet.value || breakpoints.isMobile.value;
});

const getParams = (slide: ToursCard, index: number) => ({ position: index, regionSlug: slide.slug });

onMounted(() => fetchData());

const fetchData = async () => {
  // sorting: 'filter_slugs' сохраняет порядок регионов из TOURS_REGION_SLUGS (курируемый список).
  const { data } = await getRegionsList({
    query: { slugs: TOURS_REGION_SLUGS.join(','), sorting: 'filter_slugs', ...getDisplayCurrencyQuery() },
  });

  regions.value = recursiveToCamel(data?.regions_tours ?? []);
};

const cardProps = useCarouselConfig<ToursCard>({
  slides: regions,
  tabletMatcher: 'isTabletOnly',
  mobileMini: {
    cardWidth: 300,
    spaceBetween: 8,
    options: { initialSlide: 0, slidesOffsetBefore: 20, slidesOffsetAfter: 20 },
  },
  tabletMobile: {
    slidesPerView: 2.3,
    spaceBetween: 8,
    options: {
      initialSlide: 0,
      slidesOffsetBefore: 20,
      slidesOffsetAfter: 20,
      breakpoints: { 767: { slidesPerGroup: 2 } },
    },
  },
  desktop: {
    slidesPerView: 3,
    spaceBetween: 24,
    options: { breakpoints: { 1023: { slidesPerGroup: 3 } } },
  },
});
</script>

<style lang="scss" scoped>
.title,
.subtitle {
  text-align: left;
}

.title {
  color: var(--alias-color-text-default);
  text-decoration: none;

  @include typography.base-800-750-extra;

  @include adaptivity.mobile {
    @include typography.base-700-500-extra;
  }
}

.subtitle {
  margin-top: var(--alias-space-12, 12px);

  @include typography.base-500-400-regular;

  @include adaptivity.mobile {
    margin-top: var(--alias-space-8, 8px);

    @include typography.base-400-400-regular;
  }
}

:host {
  --wrap-max-width: 1260px;
  --wrap-min-width: 950px;

  width: 100%;

  @include adaptivity.tablet {
    --wrap-max-width: 100%;
    --wrap-min-width: 100%;
  }
}

.btn {
  margin-top: var(--alias-space-40, 40px);

  @include adaptivity.tablet {
    margin-top: var(--alias-space-32, 32px);
  }

  @include adaptivity.mobile {
    margin-top: var(--alias-space-24, 24px);
  }
}

.swiper-main-container:hover {
  :deep(.btn-prev) {
    opacity: 1;
  }

  :deep(.btn-next) {
    opacity: 1;
  }
}

:deep(.btn-prev) {
  top: 35%;
  transform: translate(-50%, 0);

  @include adaptivity.tablet {
    display: none;
  }
}

:deep(.btn-next) {
  top: 35%;
  transform: translate(50%, 0);

  @include adaptivity.tablet {
    display: none;
  }
}

.mirror {
  transform: scaleX(-1);
}

.center {
  display: flex;
  justify-content: center;
}

.slide {
  flex-direction: column;
}
</style>
